// ROOM 404: Live Hibernating Chat WebSocket Service
// Single-room active subscription policy, short-lived ticket handshake, and reconnect backoff.

import { apiRequest } from './api';

/**
 * Resolves the WebSocket URL for a given room and ticket.
 * If VITE_WS_BASE_URL is absent/empty, preserves current window.location.host derivation.
 * If VITE_WS_BASE_URL is provided, normalizes protocols (https->wss, http->ws) and strips trailing slashes.
 */
export function resolveWsUrl(roomId: string, ticket: string, customBaseUrl?: string): string {
  const rawBase =
    customBaseUrl !== undefined
      ? customBaseUrl
      : (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WS_BASE_URL
          ? String(import.meta.env.VITE_WS_BASE_URL).trim()
          : '');

  const queryPart = `ticket=${encodeURIComponent(ticket)}`;
  const roomPart = `room/${encodeURIComponent(roomId)}`;

  if (rawBase) {
    let base = rawBase.replace(/\/+$/, '');
    if (base.startsWith('https://')) {
      base = 'wss://' + base.slice(8);
    } else if (base.startsWith('http://')) {
      base = 'ws://' + base.slice(7);
    } else if (!base.startsWith('wss://') && !base.startsWith('ws://')) {
      const defaultScheme =
        typeof window !== 'undefined' && window.location?.protocol === 'http:' ? 'ws://' : 'wss://';
      base = `${defaultScheme}${base.replace(/^\/+/, '')}`;
    }
    return `${base}/ws/${roomPart}?${queryPart}`;
  }

  const protocol =
    typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = typeof window !== 'undefined' ? window.location?.host : 'localhost';
  return `${protocol}//${host}/ws/${roomPart}?${queryPart}`;
}

export type SocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface ChatSocketListener {
  onMessage?: (message: any) => void;
  onAck?: (ack: { idempotencyKey: string; messageId: string; createdAt: number }) => void;
  onStatusChange?: (status: SocketStatus) => void;
  onError?: (err: { code: string; message: string }) => void;
  onRevoked?: (reason: string) => void;
}

class ChatSocketManager {
  private ws: WebSocket | null = null;
  private currentRoomId: string | null = null;
  private listeners = new Set<ChatSocketListener>();
  private pingInterval: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private status: SocketStatus = 'disconnected';

  getStatus(): SocketStatus {
    return this.status;
  }

  getCurrentRoomId(): string | null {
    return this.currentRoomId;
  }

  subscribe(listener: ChatSocketListener): () => void {
    this.listeners.add(listener);
    listener.onStatusChange?.(this.status);
    return () => this.listeners.delete(listener);
  }

  private setStatus(status: SocketStatus) {
    this.status = status;
    this.listeners.forEach((l) => l.onStatusChange?.(status));
  }

  async connectToRoom(roomId: string): Promise<void> {
    // If already connected to this room, no action required
    if (this.currentRoomId === roomId && this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    // Single active socket rule: close any existing socket immediately
    this.disconnect();
    this.currentRoomId = roomId;
    this.setStatus('connecting');

    try {
      // 1. Fetch short-lived signed WebSocket ticket from Worker
      const ticketRes = await apiRequest<{ ticket: string; roomId: string }>(
        `/api/rooms/${roomId}/ticket`,
        { method: 'POST' }
      );

      // Determine WS URL
      const wsUrl = resolveWsUrl(roomId, ticketRes.ticket);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.setStatus('connected');
        this.reconnectAttempts = 0;
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'new_message') {
            this.listeners.forEach((l) => l.onMessage?.(payload.message));
          } else if (payload.type === 'ack') {
            this.listeners.forEach((l) => l.onAck?.(payload));
          } else if (payload.type === 'revoked') {
            this.listeners.forEach((l) => l.onRevoked?.(payload.reason));
          } else if (payload.type === 'error') {
            this.listeners.forEach((l) => l.onError?.(payload));
          }
        } catch {
          // Ignored
        }
      };

      this.ws.onerror = () => {
        this.setStatus('error');
      };

      this.ws.onclose = (event) => {
        this.stopHeartbeat();
        if (event.code === 4403) {
          // Explicit permission revocation / kick - do not auto-reconnect
          this.setStatus('disconnected');
          this.listeners.forEach((l) => l.onRevoked?.('Permission revoked or kicked'));
          return;
        }

        this.setStatus('disconnected');

        // Bounded reconnection with exponential backoff (prevent reconnection loops)
        if (this.currentRoomId === roomId && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts += 1;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 8000);
          setTimeout(() => {
            if (this.currentRoomId === roomId) {
              this.connectToRoom(roomId).catch(() => {});
            }
          }, delay);
        }
      };
    } catch (err: any) {
      this.setStatus('error');
      this.listeners.forEach((l) =>
        l.onError?.({ code: 'TICKET_FAILED', message: err.message || 'Failed to obtain room ticket' })
      );
    }
  }

  sendMessage(body: string, parentMessageId?: string, attachmentId?: string): string {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Chat socket is not connected');
    }

    const idempotencyKey = crypto.randomUUID();
    const payload = {
      type: 'send_message',
      body,
      parentMessageId: parentMessageId || null,
      attachmentId: attachmentId || null,
      idempotencyKey,
    };

    this.ws.send(JSON.stringify(payload));
    return idempotencyKey;
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close(1000, 'User navigation or room change');
      } catch {
        // Ignored
      }
      this.ws = null;
    }
    this.currentRoomId = null;
    this.setStatus('disconnected');
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000); // 25s ping
  }

  private stopHeartbeat(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

export const chatSocket = new ChatSocketManager();
